import { DateTime } from 'luxon'
import React, { useEffect } from 'react'
import ReactRouterPropTypes from 'react-router-prop-types'
import { NetworkStatus } from 'apollo-client'
import { Query } from 'react-apollo'
import { Redirect } from 'react-router-dom'

import Progress from '../Progress/Progress'
import CloseButton from '../CloseButton/CloseButton'
import { GET_COMPLETED_ORDER } from '../../apollo/cart/cart.graphql'
import { RestaurantConsumer } from '../RestaurantProvider/RestaurantProvider'
import RestaurantAddress from '../RestaurantAddress/RestaurantAddress'
import Fieldset from '../Modifiers/Fieldset/Fieldset'
import { FlagOn } from '../FeatureFlag/FeatureFlag'
import { BackToMenuButton } from './BackToMenuButton/BackToMenuButton'
import CartTable from '../Cart/CartTable/CartTable'
import CartFooter from '../Cart/CartFooter/CartFooter'
import { LoyaltyConfirmation } from '../LoyaltyConfirmation/LoyaltyConfirmation'
import ScrollToTop from '../ScrollToTop/ScrollToTop'
import { toRelativeDateString } from '../../utils/time-utils'
import { useAuth } from '../AuthProvider/AuthProvider'
import { formatPhoneNumber } from '../../utils/phone-number'
import { SocialBanner } from './SocialBanner/SocialBanner'
import { GuestFeedback } from '../GuestFeedback/GuestFeedback'

import './ConfirmPage.css'
import { GuestCommunication } from '../GuestCommunication/GuestCommunication'

const formatCurbsideV2Info = curbsidePickupV2 => {
  const elements = [
    curbsidePickupV2.transportColor,
    curbsidePickupV2.transportDescription
  ]

  return elements
    .filter(element => Boolean(element))
    .join(' ')
}

/**
 * @param {DateTime} timestamp Luxon DateTime object
 * @returns {string} formatted hours for display
 */
const formatHours = timestamp => timestamp
  .toLocaleString(DateTime.TIME_SIMPLE)

const ConfirmPage = ({ match }) => {
  const { clearUnverifiedCustomer } = useAuth()

  useEffect(function clearUnverifiedCustomerOnUnmount () {
    return () => {
      clearUnverifiedCustomer()
    }
  }, [clearUnverifiedCustomer])

  return (
    <RestaurantConsumer>
      {({ getRestaurantPath, restaurantGuid }) => (
        <div styleName='confirmPage'>
          <ScrollToTop />
          <Query query={GET_COMPLETED_ORDER} variables={{ input: {
            orderGuid: match.params.orderGuid,
            restaurantGuid
          } }}>
            {({ loading, error, data, networkStatus }) => {
              if (loading || networkStatus === NetworkStatus.refetch) return <Progress eventData={{ category: 'Confirm Page' }} />
              if (!data || !data.completedOrder || error) return <Redirect to={getRestaurantPath()} />
              const completedOrder = data.completedOrder
              const {
                checkNumber,
                estimatedFulfillmentDate,
                guestCommunication,
                restaurant: {
                  whiteLabelName,
                  location,
                  creditCardConfig: { tipEnabled },
                  loyaltyConfig,
                  socialMediaLinks
                },
                paymentType,
                tip,
                outpostOrder,
                deliveryInfo,
                curbsidePickup,
                curbsidePickupV2
              } = completedOrder
              const { address1, address2, city, state, phone } = location
              const firstName = completedOrder.customerV2 && completedOrder.customerV2.firstName
              const cart = {
                // TODO Add diningOptionBehavior here if we update the BFF to return that.
                order: completedOrder
              }
              const curbsideSelected = (curbsidePickup && curbsidePickup.selected) || Boolean(curbsidePickupV2)

              const loyaltyEnabled = loyaltyConfig.loyaltySignupEnabled || loyaltyConfig.loyaltyRedemptionEnabled
              const canShowTip = paymentType === 'CREDIT' && tipEnabled
              const fulfillmentDateTime = DateTime.fromMillis(estimatedFulfillmentDate)
              return (
                <React.Fragment>
                  <div>
                    <div styleName='information'>
                      <h2 data-testid='order-sent-header' styleName='heading'>Order Sent</h2>
                      <FlagOn name='oo-v3-social-sharing'>
                        <SocialBanner socialMediaLinks={socialMediaLinks} whiteLabelName={whiteLabelName} />
                      </FlagOn>
                      {outpostOrder ? (
                        <p data-testid='confirmation-text' styleName='confirmation'>
                          {firstName ? `Thanks ${firstName}!` : 'Thanks!'}<br />
                          Your order will arrive around
                        </p>
                      ) : (
                        <p data-testid='confirmation-text' styleName='confirmation'>
                          {firstName ? `Thanks ${firstName}!` : 'Thanks!'} <br />
                          {`Your order will be ${deliveryInfo ? 'delivered' : 'ready'}`}
                        </p>
                      )}
                      <p data-testid='order-ready-time' styleName='time'>
                        {toRelativeDateString(fulfillmentDateTime)} at {formatHours(fulfillmentDateTime)}
                      </p>
                      {curbsideSelected && <p styleName='confirmation' data-testid='curbside-text'>
                        for curbside pickup.
                      </p>}
                      {curbsidePickupV2 && curbsidePickupV2.transportDescription && (
                        <p data-testid='curbside-info-wrapper' styleName='text'>
                          <strong>Your vehicle: </strong>
                          <span data-testid='curbside-info'>{formatCurbsideV2Info(curbsidePickupV2)}</span>
                        </p>
                      )}
                      {deliveryInfo && (
                        <p data-testid='delivery-info' styleName='confirmation'>
                          to {deliveryInfo.address1}{deliveryInfo.address2 ? ` ${deliveryInfo.address2}` : ''}, <br />
                          {deliveryInfo.city}, {deliveryInfo.state} {deliveryInfo.zipCode}
                        </p>
                      )}
                      {deliveryInfo && deliveryInfo.notes && (
                        <p data-testid='delivery-note-wrapper' styleName='text'>
                          <strong>Your instructions: </strong>
                          <span data-testid='delivery-note'>{deliveryInfo.notes}</span>
                        </p>
                      )}

                    </div>
                    <div styleName='emailConfirmationWrapper'>
                      <p data-testid='email-confirmation-send-message' styleName='text'>An email receipt has been
                      sent.</p>
                      <GuestCommunication curbsideSelected={curbsideSelected} guestCommunication={guestCommunication} phone={phone} />
                      <h2 data-testid='restaurant-name' styleName='name'>{whiteLabelName}</h2>
                      <p styleName='text'>
                        <RestaurantAddress address1={address1} address2={address2} city={city} state={state} />
                      </p>
                      <p styleName='text'>Issue with your order? Call {whiteLabelName} <a
                        href={`tel:${phone}`}>{formatPhoneNumber(phone)}</a></p>
                    </div>
                    <FlagOn name='oo-v3-loyalty'>
                      {loyaltyEnabled &&
                        <LoyaltyConfirmation
                          orderGuid={match.params.orderGuid}
                        />
                      }
                    </FlagOn>
                    <FlagOn name='oo-v3-guest-feedback'>
                      <GuestFeedback orderGuid={match.params.orderGuid} />
                    </FlagOn>
                    <Fieldset label='Your Order' instructions={checkNumber && `Check #${checkNumber}`} collapsable collapsed>
                      <CartTable cart={cart} editable={false} />
                    </Fieldset>
                    <div styleName='cartFooter'>
                      <CartFooter
                        cart={cart}
                        deliveryChargeLabel='Delivery Cost'
                        showTotal
                        showTip={canShowTip}
                        tip={(canShowTip && parseFloat(tip)) || null}
                      />
                    </div>
                    <BackToMenuButton getRestaurantPath={getRestaurantPath} />
                  </div>
                  <div styleName='closeButton'>
                    <CloseButton data-testid='close-button-order-sent-page' to={getRestaurantPath()} />
                  </div>
                </React.Fragment>
              )
            }}
          </Query>
        </div>
      )}
    </RestaurantConsumer>
  )
}

ConfirmPage.propTypes = {
  match: ReactRouterPropTypes.match.isRequired
}

export default ConfirmPage
